"use client";

import { useState, useEffect } from "react";
import { getInstance } from "@opencampus/ochub-utils";

interface AuthState {
  OCId?: string;
  ethAddress?: string;
}

// Fun learner jargons for non-logged-in users
const LEARNER_JARGONS = [
  "Knowledge Seeker",
  "Curious Explorer",
  "Wisdom Hunter",
  "Brain Athlete",
  "Info Ninja",
  "Study Warrior",
  "Learning Legend",
  "Wiki Wanderer",
  "Fact Finder",
  "Mind Expander",
  "Knowledge Nomad",
  "Trivia Titan",
  "Discovery Diver",
  "Insight Adventurer",
];

/**
 * Hook to use OCAccount from ochub-utils
 * Returns auth state and a fun learner jargon for non-logged-in users
 */
export function useOCAccount() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [ocId, setOcId] = useState<string | undefined>();
  const [isInitialized, setIsInitialized] = useState(false);
  const [learnerJargon] = useState(
    () => LEARNER_JARGONS[Math.floor(Math.random() * LEARNER_JARGONS.length)],
  );

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    try {
      const sdkOpts = {
        storageType: "cookie",
        sameSite: false,
      };

      const ocAccount = getInstance({ sandboxMode: true, opts: sdkOpts });

      // Get initial state
      const authState = ocAccount.getAuthState();

      setIsLoggedIn(ocAccount.isAuthenticated());
      setOcId(authState.OCId);
      setIsInitialized(true);

      // Subscribe to changes
      unsubscribe = ocAccount.subscribe((newAuthState: AuthState) => {
        setIsLoggedIn(!!newAuthState.OCId);
        setOcId(newAuthState.OCId);
      });
    } catch (error) {
      // SDK not available or failed to initialize - that's OK
      console.log("OCAccount not available:", error);
      setIsInitialized(true);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  return {
    isLoggedIn,
    ocId,
    isInitialized,
    displayName: isLoggedIn && ocId ? ocId : learnerJargon,
    learnerJargon,
  };
}
